import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../providers/AuthProvider";
import { storage } from "../../../shared/api/storage";
import {
  AUTH_REDIRECT_KEY,
  AUTH_LINK_PROVIDER_KEY,
  PROFILE_NOTICE_KEY,
} from "../../../../providers/AuthProvider";
import { linkGoogleAccount, linkFacebookAccount, ApiError } from "../../api/auth";
import { WarningCircle } from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";
import { createLogger } from "../../../../utils/logger";

const logger = createLogger("OAuthCallbackPage");

export function OAuthCallbackPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOAuthCallback, accessToken, refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      // Handle OAuth errors (user denied access, etc.)
      if (error) {
        setError(t("auth.oauth.failed", { error }));
        setTimeout(() => navigate("/login", { replace: true }), 3000);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setError(t("auth.oauth.invalid_callback"));
        setTimeout(() => navigate("/login", { replace: true }), 3000);
        return;
      }

      try {
        const linkProvider = storage.get<string>(AUTH_LINK_PROVIDER_KEY);
        if (linkProvider) {
          if (!accessToken) {
            storage.set(PROFILE_NOTICE_KEY, {
              type: "error",
              message: t("auth.oauth.signin_before_link"),
            });
            storage.remove(AUTH_LINK_PROVIDER_KEY);
            const redirectPath =
              storage.get<string>(AUTH_REDIRECT_KEY) || "/login";
            storage.remove(AUTH_REDIRECT_KEY);
            navigate(redirectPath, { replace: true });
            return;
          }

          try {
            switch (linkProvider) {
              case "google":
                await linkGoogleAccount(accessToken, {
                  code,
                  state,
                  provider: "google"
                });
                storage.set(PROFILE_NOTICE_KEY, {
                  type: "success",
                  message: t("auth.oauth.google_linked"),
                });
                await refreshUser();
                break;
              case "facebook":
                await linkFacebookAccount(accessToken, {
                  code,
                  state,
                  provider: "facebook"
                });
                storage.set(PROFILE_NOTICE_KEY, {
                  type: "success",
                  message: t("auth.oauth.facebook_linked"),
                });
                await refreshUser();
                break;
              default:
                storage.set(PROFILE_NOTICE_KEY, {
                  type: "error",
                  message: t("auth.oauth.unsupported_provider"),
                });
            }
          } catch (linkErr: unknown) {
            logger.error("OAuth link error:", linkErr);
            const message =
              linkErr instanceof ApiError
                ? linkErr.message
                : t("auth.oauth.link_error");
            storage.set(PROFILE_NOTICE_KEY, {
              type: "error",
              message,
            });
          } finally {
            storage.remove(AUTH_LINK_PROVIDER_KEY);
          }

          const redirectPath =
            storage.get<string>(AUTH_REDIRECT_KEY) || "/settings/profile";
          storage.remove(AUTH_REDIRECT_KEY);
          navigate(redirectPath, { replace: true });
          return;
        }

        // Process the OAuth callback
        await handleOAuthCallback(code, state);

        // Redirect to stored path or dashboard
        const redirectPath = storage.get<string>(AUTH_REDIRECT_KEY) || "/dashboard";
        storage.remove(AUTH_REDIRECT_KEY);
        navigate(redirectPath, { replace: true });
      } catch (err) {
        logger.error("OAuth callback error:", err);
        setError(t("auth.oauth.signin_error"));
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate, handleOAuthCallback, accessToken, refreshUser, t]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-2xl">
        {error ? (
          <div className="flex flex-col items-center gap-4">
            <WarningCircle
              {...defaultIconProps}
              className="h-12 w-12 text-rose-600"
              aria-hidden
            />
            <h2 className="text-2xl font-semibold text-rose-700">
              {t("auth.oauth.error_title")}
            </h2>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-sm text-slate-500">
              {t("auth.oauth.redirecting")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
              <div
                className="h-12 w-12 animate-spin motion-reduce:animate-none rounded-full border-4 border-slate-200 border-t-slate-600"
                aria-hidden
              />
            <h2 className="text-2xl font-semibold text-slate-900">
              {t("auth.oauth.processing")}
            </h2>
          </div>
        )}
      </div>
    </div>
  );
}
